/**
 * Recognizes errors that mean "this tab is running a build the server no longer
 * serves", or that a request was dropped in flight — not application bugs.
 *
 * After a deploy, an open tab keeps running the old client bundle. Its next
 * client-side navigation or revalidation asks for a hashed chunk or a `.data`
 * payload from the previous build and gets one of these instead:
 *
 * - "No result found for routeId ..." — the new server's data response has no
 *   entry for a route id taken from the old manifest.
 * - "Failed to fetch" / "Load failed" — the asset or data URL is gone, or the
 *   machine was suspended/restarting when the request went out (this app runs
 *   with Fly `auto_stop_machines = 'suspend'`).
 * - Module import failures — a lazy route chunk no longer exists.
 *
 * All are cured by loading the current build, so the tab reloads itself once
 * instead of showing a server-error screen and paging admins over Discord.
 */
export function isStaleBuildOrNetworkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  if (!message) return false;

  return (
    /No result found for routeId/i.test(message) ||
    /Failed to fetch/i.test(message) ||
    /Load failed/i.test(message) || // Safari's wording for a failed fetch
    /NetworkError/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Failed to load module script/i.test(message)
  );
}
